import React, { useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Grid2,
	Stack,
	Typography
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import HtmlEditor from "../../components/UI/HtmlEditor";
import AssistantNotesPanel from "../../components/internal-user/presentation-drafts/AssistantNotesPanel";
import PublishPresentationDraftDialog from "../../components/internal-user/presentation-drafts/PublishPresentationDraftDialog";
import RegeneratePresentationDraftDialog from "../../components/internal-user/presentation-drafts/RegeneratePresentationDraftDialog";
import TranscriptDialog from "../../components/internal-user/presentation-drafts/TranscriptDialog";
import { useFreelancerProfileByKey } from "../../hooks/useFreelancerProfile";
import {
	useDiscardPresentationDraft,
	usePresentationDrafts,
	usePublishPresentationDraft,
	useRegeneratePresentationDraft,
	useRetryPresentationDraft
} from "../../hooks/usePresentationDrafts";
import { presentationDraftKey, usePresentationDraftAutosave } from "../../hooks/usePresentationDraftAutosave";
import { PresentationDraft, PresentationDraftStatus } from "../../models/PresentationDraft";
import { LanguageCode, toLanguageCode } from "../../models/enums/LanguageCode";
import { zoullooColors } from "../../theme";
import { formatDate } from "../../utils/formatting";
import { MAX_CONTENT_LENGTH, findUnfinishedPlaceholders, getOpenDraft, talentDetailsPath } from "../../utils/presentationDraft";

type ReviewDialog = "regenerate" | "publish" | "discard" | "transcript" | null;

const TIMEOUT_FAILURE_REASON = "Timeout";

const statusColor = (status: PresentationDraftStatus) => {
	switch (status) {
		case PresentationDraftStatus.Published:
			return "success";
		case PresentationDraftStatus.Failed:
			return "error";
		case PresentationDraftStatus.Discarded:
			return "default";
		default:
			return "secondary";
	}
};

const PresentationDraftReview: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { key = "" } = useParams();
	const { data: freelancerProfile, isFetching: isProfileFetching } = useFreelancerProfileByKey(key);
	const freelancerProfileId = freelancerProfile?.id ?? 0;
	const { data: drafts, isLoading: areDraftsLoading } = usePresentationDrafts(freelancerProfileId);

	const [selectedDraftId, setSelectedDraftId] = useState<number>();
	const [dialog, setDialog] = useState<ReviewDialog>(null);

	const selectedDraft: PresentationDraft | undefined =
		drafts?.find(draft => draft.id === selectedDraftId) ?? getOpenDraft(drafts) ?? drafts?.[0];

	const autosave = usePresentationDraftAutosave(selectedDraft);
	const regenerateDraft = useRegeneratePresentationDraft();
	const retryDraft = useRetryPresentationDraft();
	const publishDraft = usePublishPresentationDraft();
	const discardDraft = useDiscardPresentationDraft();

	const talentFirstName = freelancerProfile?.freelancer?.user?.firstName ?? "";
	const backToTalent = () => navigate(talentDetailsPath(key));

	if (!freelancerProfile && !isProfileFetching) {
		return (
			<Box px={{ xs: 2, sm: 4 }} py={3}>
				<Typography variant="body1" color="text.secondary">
					{t("presentationDraft.review.talentNotFound")}
				</Typography>
			</Box>
		);
	}

	if (!freelancerProfile || areDraftsLoading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
				<CircularProgress color="secondary" />
			</Box>
		);
	}

	const isEditable = selectedDraft?.status === PresentationDraftStatus.Generated;
	const placeholders = isEditable ? findUnfinishedPlaceholders(autosave.content) : [];
	const canPublish = isEditable && placeholders.length === 0 && !autosave.isTooLong && autosave.content.trim() !== "";

	const runAfterSave = async (action: () => void) => {
		if (await autosave.flush()) {
			action();
		}
	};

	const handleRegenerate = (instruction: string) => {
		if (!selectedDraft) return;
		void runAfterSave(() =>
			regenerateDraft.mutate(
				{ draftId: selectedDraft.id, instruction },
				{
					onSuccess: draft => {
						setSelectedDraftId(draft.id);
						setDialog(null);
					}
				}
			)
		);
	};

	const handlePublish = (isExemplary: boolean) => {
		if (!selectedDraft) return;
		void runAfterSave(() =>
			publishDraft.mutate({ draftId: selectedDraft.id, isExemplary }, { onSuccess: backToTalent })
		);
	};

	const handleDiscard = () => {
		if (!selectedDraft) return;
		discardDraft.mutate(selectedDraft.id, { onSuccess: backToTalent });
	};

	const languageLabel = (languageCode: string) =>
		t(toLanguageCode(languageCode) === LanguageCode.En ? "presentationDraft.languages.en" : "presentationDraft.languages.fr");

	const renderSaveState = () => {
		if (autosave.hasSaveFailed) {
			return t("presentationDraft.review.saveFailed");
		}
		if (autosave.isSaving) {
			return t("presentationDraft.review.saving");
		}
		if (autosave.savedAt) {
			return t("presentationDraft.review.savedAt", { time: format(autosave.savedAt, "HH:mm") });
		}
		return t("presentationDraft.review.autosaveHint");
	};

	const renderGenerating = () => (
		<Stack
			spacing={2}
			alignItems="center"
			sx={{ py: 8, px: 3, textAlign: "center", border: `1px dashed ${zoullooColors.border.dashed}`, borderRadius: "12px" }}
		>
			<CircularProgress color="secondary" />
			<Typography variant="h4">{t("presentationDraft.review.generatingTitle")}</Typography>
			<Typography variant="body2" color="text.secondary">
				{t("presentationDraft.review.generatingHint")}
			</Typography>
		</Stack>
	);

	const renderFailed = (draft: PresentationDraft) => (
		<Alert
			severity="error"
			action={
				<Stack direction="row" spacing={1}>
					<Button color="inherit" size="small" onClick={() => setDialog("discard")}>
						{t("presentationDraft.review.discard")}
					</Button>
					<Button
						color="inherit"
						size="small"
						variant="outlined"
						disabled={retryDraft.isPending}
						onClick={() => retryDraft.mutate(draft.id)}
					>
						{t("presentationDraft.entry.retry")}
					</Button>
				</Stack>
			}
		>
			{draft.failureReason === TIMEOUT_FAILURE_REASON
				? t("presentationDraft.review.failedTimeout")
				: t("presentationDraft.review.failed")}
		</Alert>
	);

	const renderEditor = (draft: PresentationDraft) => (
		<Stack spacing={2}>
			{draft.status === PresentationDraftStatus.Published && (
				<Alert severity="success">
					{t("presentationDraft.review.publishedOn", { date: formatDate(draft.publishedAt ?? undefined) })}
					{draft.isExemplary && ` · ${t("presentationDraft.review.exemplary")}`}
				</Alert>
			)}
			{draft.status === PresentationDraftStatus.Discarded && (
				<Alert severity="info">{t("presentationDraft.review.discardedInfo")}</Alert>
			)}
			{placeholders.length > 0 && (
				<Alert severity="warning">
					{t("presentationDraft.review.placeholders", { count: placeholders.length })}
					<Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
						{placeholders.map((placeholder, index) => (
							<li key={`${placeholder}-${index}`}>{placeholder}</li>
						))}
					</Box>
				</Alert>
			)}
			{autosave.isTooLong && (
				<Alert severity="error">{t("presentationDraft.review.tooLong", { max: MAX_CONTENT_LENGTH })}</Alert>
			)}
			<HtmlEditor
				key={presentationDraftKey(draft)}
				readonly={!isEditable}
				emitUpdatesOnlyWhenFocused
				showCounter={false}
				content={draft.content}
				onUpdate={({ editor }) => autosave.edit(editor.getHTML())}
				sx={{
					"& .MuiTiptap-FieldContainer-root .MuiTiptap-RichTextContent-root .ProseMirror": {
						height: "min(60vh, 560px) !important"
					}
				}}
			/>
			{isEditable && (
				<Typography variant="caption" color={autosave.hasSaveFailed ? "error" : "text.secondary"}>
					{renderSaveState()}
				</Typography>
			)}
		</Stack>
	);

	const renderDraft = (draft: PresentationDraft) => {
		switch (draft.status) {
			case PresentationDraftStatus.Generating:
				return renderGenerating();
			case PresentationDraftStatus.Failed:
				return renderFailed(draft);
			default:
				return renderEditor(draft);
		}
	};

	const chronologicalDrafts = [...(drafts ?? [])].reverse();

	return (
		<Box px={{ xs: 2, sm: 4 }} py={3}>
			<Button startIcon={<ArrowBackIcon />} onClick={backToTalent} sx={{ mb: 2 }}>
				{t("presentationDraft.review.back", { name: talentFirstName })}
			</Button>

			<Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 1 }}>
				<Typography variant="h2">{t("presentationDraft.review.title", { name: talentFirstName })}</Typography>
				{selectedDraft && (
					<Chip size="small" label={languageLabel(selectedDraft.languageCode)} variant="outlined" />
				)}
			</Box>

			{!selectedDraft ? (
				<Typography variant="body1" color="text.secondary" sx={{ mt: 3 }}>
					{t("presentationDraft.review.empty")}
				</Typography>
			) : (
				<>
					<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
						{chronologicalDrafts.map(draft => (
							<Chip
								key={draft.id}
								size="small"
								clickable
								onClick={() => void runAfterSave(() => setSelectedDraftId(draft.id))}
								color={statusColor(draft.status)}
								variant={draft.id === selectedDraft.id ? "filled" : "outlined"}
								label={t("presentationDraft.review.versionLabel", {
									version: draft.version,
									status: t(`presentationDraft.status.${draft.status}`)
								})}
							/>
						))}
					</Box>

					<Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 2 }}>
						{t("presentationDraft.review.stamp", { date: formatDate(selectedDraft.createdAt) })}
						{selectedDraft.hasManualEdits && ` · ${t("presentationDraft.review.manuallyEdited")}`}
					</Typography>

					{selectedDraft.extraInstruction && (
						<Alert severity="info" icon={false} sx={{ mb: 2 }}>
							<strong>{t("presentationDraft.review.instruction")}</strong> {selectedDraft.extraInstruction}
						</Alert>
					)}

					<Grid2 container spacing={3}>
						<Grid2 size={{ xs: 12, lg: 8 }}>{renderDraft(selectedDraft)}</Grid2>
						<Grid2 size={{ xs: 12, lg: 4 }}>
							<AssistantNotesPanel notes={selectedDraft.assistantNotes} onShowTranscript={() => setDialog("transcript")} />
						</Grid2>
					</Grid2>

					{isEditable && (
						<Box
							sx={{
								display: "flex",
								justifyContent: "flex-end",
								gap: 1.5,
								flexWrap: "wrap",
								mt: 3,
								pt: 2,
								borderTop: `1px solid ${zoullooColors.grey[300]}`
							}}
						>
							<Button onClick={() => setDialog("discard")}>{t("presentationDraft.review.discard")}</Button>
							<Button variant="outlined" color="secondary" onClick={() => setDialog("regenerate")}>
								{t("presentationDraft.review.regenerate")}
							</Button>
							<Button variant="contained" color="secondary" disabled={!canPublish} onClick={() => setDialog("publish")}>
								{t("presentationDraft.review.publish")}
							</Button>
						</Box>
					)}

					<TranscriptDialog open={dialog === "transcript"} draftId={selectedDraft.id} onClose={() => setDialog(null)} />
				</>
			)}

			{dialog === "regenerate" && (
				<RegeneratePresentationDraftDialog
					open
					isSubmitting={regenerateDraft.isPending}
					onClose={() => setDialog(null)}
					onSubmit={handleRegenerate}
				/>
			)}

			{dialog === "publish" && (
				<PublishPresentationDraftDialog
					open
					isSubmitting={publishDraft.isPending}
					talentName={talentFirstName}
					onClose={() => setDialog(null)}
					onConfirm={handlePublish}
				/>
			)}

			<Dialog open={dialog === "discard"} onClose={() => setDialog(null)}>
				<DialogTitle>{t("presentationDraft.discard.title")}</DialogTitle>
				<DialogContent>
					<DialogContentText>{t("presentationDraft.discard.message")}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDialog(null)} disabled={discardDraft.isPending}>
						{t("common.cancel")}
					</Button>
					<Button variant="contained" color="secondary" onClick={handleDiscard} disabled={discardDraft.isPending}>
						{t("presentationDraft.discard.confirm")}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default PresentationDraftReview;
